import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from "react";
import { DOB_INPUT_DATE } from "@jti/fe-common";
import { useConfig } from "../../providers/useConfig";
import { useTranslations } from "../../providers/useTranslations";
import { getDaysInMonth } from "../../utils/date";
import { formatDate } from "../../utils/formatDate/formatDate";
import { getNewDaysArray } from "../DEPRECATED_CustomSelect";
import { DEPRECATED_TextInput } from "../DEPRECATED_TextInput";
import { FormField, useFormContext } from "../Form";
import styles from "./DateInputs.module.scss";
const minPossibleYear = 1900;
export const DateInputs = ({ fieldsPrefix, fieldName, yearsRange, isDateChangeRestricted }) => {
    const { watch, setValue, register, trigger, formState: { errors, dirtyFields }, } = useFormContext();
    const { __ } = useTranslations();
    const { dobFormType } = useConfig();
    const dayInput = useRef(null);
    const monthInput = useRef(null);
    const yearInput = useRef(null);
    const dobDay = watch(`${fieldsPrefix}.dobDay`);
    const dobMonth = watch(`${fieldsPrefix}.dobMonth`);
    const dobYear = watch(`${fieldsPrefix}.dobYear`);
    const dobError = errors[fieldsPrefix];
    const preparedDobError = errors[fieldName];
    const dayError = dobError?.["dobDay"];
    const monthError = dobError?.["dobMonth"];
    const yearError = dobError?.["dobYear"];
    const isFormDateInvalid = dayError || monthError || yearError;
    const daysInSelectedMonth = useMemo(() => {
        const daysInMonth = getDaysInMonth(dobMonth, dobYear);
        const daysArray = getNewDaysArray(daysInMonth);
        return daysArray;
    }, [dobMonth, dobYear]);
    const handleAgeInputKeyUp = (event) => {
        const inputValue = event.target.value;
        const valueLength = inputValue.length;
        const inputName = event.target.name;
        if (Number(inputValue) < 0 || isNaN(Number(inputValue))) {
            setValue(inputName, "");
            trigger(inputName);
            return;
        }
        const inputsList = [dayInput.current, monthInput.current, yearInput.current];
        const activeInputIndex = inputsList.indexOf(event.target);
        if (!valueLength &&
            event.key === "Backspace" &&
            inputValue === inputsList[activeInputIndex].value &&
            activeInputIndex !== 0) {
            inputsList[activeInputIndex - 1].focus({ preventScroll: true });
            event.preventDefault();
        }
        if (valueLength >= event.target.maxLength && inputValue === inputsList[activeInputIndex].value) {
            trigger(`${fieldsPrefix}.dobDay`);
            if (activeInputIndex !== inputsList.length - 1) {
                inputsList[activeInputIndex + 1].focus({ preventScroll: true });
                event.preventDefault();
            }
        }
    };
    useEffect(() => {
        const isDobHasValue = dobDay && dobMonth && dobYear;
        const isDateInvalid = isNaN(dobDay) || isNaN(dobMonth) || isNaN(dobYear);
        const isFullYear = dobYear?.length === 4;
        if (isDobHasValue && !isDateInvalid && isFullYear) {
            const date = new Date(dobYear, dobMonth - 1, dobDay);
            const formattedDate = formatDate(date, DOB_INPUT_DATE);
            setValue(fieldName, formattedDate);
            return;
        }
        setValue(fieldName, "");
        if (preparedDobError ||
            isFormDateInvalid ||
            (Object.keys(dirtyFields[fieldsPrefix] ?? {}).length === 3 && !preparedDobError && isFullYear)) {
            trigger(fieldName);
        }
    }, [
        dobDay,
        dobMonth,
        dobYear,
        fieldName,
        setValue,
        preparedDobError?.message,
        dirtyFields,
        fieldsPrefix,
        isFormDateInvalid,
    ]);
    useEffect(() => {
        register(fieldName);
    }, [fieldName, register]);
    const dobInputs = [
        _jsx(FormField, { name: `${fieldsPrefix}.dobDay`, hasEmptyError: Boolean(dayError || dobError || preparedDobError) && dayError?.type !== "required", dataTestId: "dobDayInputWrapper", render: ({ field }) => (_jsx(DEPRECATED_TextInput, { ...field, placeholder: __("auth.age_verification.method_fields.dob_day.select_placeholder"), dataTestId: "dobDayInput", disabled: isDateChangeRestricted, ref: (dayElement) => {
                    field.ref(dayElement);
                    dayInput.current = dayElement;
                }, type: "tel", maxLength: 2, className: styles.dateInput, onKeyUp: handleAgeInputKeyUp })), validation: {
                validate: {
                    isDayValid: (day) => Number(day) >= 1 && Number(day) <= Number(daysInSelectedMonth.at(-1).value),
                },
            } }, "dayDobInput"),
        _jsx(FormField, { name: `${fieldsPrefix}.dobMonth`, hasEmptyError: Boolean(monthError || dobError || preparedDobError) && monthError?.type !== "required", dataTestId: "dobMonthInputWrapper", render: ({ field }) => (_jsx(DEPRECATED_TextInput, { ...field, placeholder: __("auth.age_verification.method_fields.dob_month.select_placeholder"), dataTestId: "dobMonthInput", disabled: isDateChangeRestricted, ref: (monthElement) => {
                    field.ref(monthElement);
                    monthInput.current = monthElement;
                }, type: "tel", maxLength: 2, className: styles.dateInput, onKeyUp: handleAgeInputKeyUp })), validation: {
                validate: {
                    isMonthValid: (month) => Number(month) >= 1 && Number(month) <= 12,
                },
            } }, "monthDobInput"),
        _jsx(FormField, { name: `${fieldsPrefix}.dobYear`, hasEmptyError: Boolean(yearError || dobError || preparedDobError) && yearError?.type !== "required", dataTestId: "dobYearInputWrapper", render: ({ field }) => (_jsx(DEPRECATED_TextInput, { ...field, placeholder: __("auth.age_verification.method_fields.dob_year.select_placeholder"), dataTestId: "dobYearInput", disabled: isDateChangeRestricted, ref: (yearElement) => {
                    field.ref(yearElement);
                    yearInput.current = yearElement;
                }, type: "tel", maxLength: 4, className: styles.dateInput, onKeyUp: handleAgeInputKeyUp })), validation: {
                validate: {
                    isYearValid: (year) => Number(year) <= new Date().getFullYear() &&
                        Number(year) <= Number(yearsRange[0].value) &&
                        Number(year) >= minPossibleYear,
                },
            } }, "yearDobInput"),
    ];
    return (_jsx("div", { className: styles.dateInputsContainer, children: dobFormType === "inputs_reversed" ? dobInputs.toReversed() : dobInputs }));
};
//# sourceMappingURL=DateInputs.js.map